import React, { useState } from 'react';
import { useStore } from '../../../store/useStore';
import { Image as ImageIcon, UploadCloud, Folder, MoreVertical, Plus, Tag, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { UploadModal } from '../../ui/UploadModal';

export const GaleriaFotos: React.FC = () => {
  const { mediaLibrary, setMediaLibrary } = useStore();
  const [activeFolder, setActiveFolder] = useState('Todas as Fotos');
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const folders = ['Todas as Fotos', 'Fachada', 'Recepção', 'Equipe', 'Bastidores'];

  // Mock data fallback
  const mockPhotos = [
    { id: '1', title: 'Fachada Dia', folder: 'Fachada', thumb: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=400&q=80' },
    { id: '2', title: 'Recepção', folder: 'Recepção', thumb: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=400&q=80' },
    { id: '3', title: 'Reunião de Equipe', folder: 'Equipe', thumb: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=400&q=80' },
    { id: '4', title: 'Fachada Noite', folder: 'Fachada', thumb: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=400&q=80' },
    { id: '5', title: 'Bastidores Produção', folder: 'Bastidores', thumb: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=400&q=80' },
  ];

  const allPhotos = mediaLibrary.fotosEstruturais?.length > 0 ? mediaLibrary.fotosEstruturais : mockPhotos;
  const filteredPhotos = activeFolder === 'Todas as Fotos' ? allPhotos : allPhotos.filter((p: any) => p.folder === activeFolder);

  const toggleSelect = (id: string) => {
    setSelectedPhotos(prev => 
      prev.includes(id) ? prev.filter(pId => pId !== id) : [...prev, id]
    );
  };

  const handleFileUpload = (files: File[], title: string) => {
    if (files.length > 0) {
      const newPhotos = files.map((file, idx) => ({
        id: Math.random().toString(36).substr(2, 9),
        title: files.length > 1 ? `${title} ${idx + 1}` : title,
        folder: activeFolder === 'Todas as Fotos' ? 'Bastidores' : activeFolder,
        thumb: URL.createObjectURL(file)
      }));
      
      setMediaLibrary({
        fotosEstruturais: [...allPhotos, ...newPhotos]
      });
      toast.success(`${newPhotos.length} foto(s) enviada(s) com sucesso!`);
    }
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      <UploadModal 
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Enviar Novas Fotos"
        accept="image/*"
        multiple
        onUpload={handleFileUpload}
      />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Galeria de Fotos</h3>
          <p className="text-sm text-gray-500 mt-1">Fotos estruturais, fachada, equipe. Traz tangibilidade ao seu negócio.</p>
        </div>
        <div className="flex gap-3">
          {selectedPhotos.length > 0 && (
            <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              Mover ({selectedPhotos.length})
            </button>
          )}
          <button onClick={() => setIsUploadOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
            <UploadCloud size={16} />
            Nova Foto
          </button>
        </div>
      </div>

      <div className="flex gap-6 h-[500px]">
        {/* Sidebar Folders */}
        <div className="w-56 shrink-0 flex flex-col gap-1 border-r border-gray-100 pr-6">
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-3">Álbuns</h4>
          {folders.map(folder => (
            <button
              key={folder}
              onClick={() => setActiveFolder(folder)}
              className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${activeFolder === folder ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <div className="flex items-center gap-2">
                <Folder size={16} className={activeFolder === folder ? 'text-indigo-500' : 'text-gray-400'} />
                {folder}
              </div>
            </button>
          ))}
          
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 mt-2 transition-colors border border-dashed border-gray-300">
            <Plus size={16} />
            Novo Álbum
          </button>
        </div>

        {/* Photo Grid */}
        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredPhotos.map((photo: any) => {
              const isSelected = selectedPhotos.includes(photo.id);
              return (
                <div 
                  key={photo.id} 
                  onClick={() => toggleSelect(photo.id)}
                  className={`group relative aspect-square rounded-xl overflow-hidden cursor-pointer transition-all ${isSelected ? 'ring-2 ring-indigo-500 ring-offset-2' : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1'}`}
                >
                  <img src={photo.thumb} alt={photo.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className={`absolute inset-0 transition-colors ${isSelected ? 'bg-indigo-500/20' : 'bg-black/0 group-hover:bg-black/10'}`} />
                  
                  {/* Select Checkbox Indicator */}
                  <div className={`absolute top-2 left-2 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-white/70 bg-black/20 opacity-0 group-hover:opacity-100'}`}>
                    {isSelected && <CheckCircle2 size={12} className="text-white" />}
                  </div>

                  {/* Info Overlay on Hover */}
                  <div className={`absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                    <p className="text-white text-xs font-medium truncate">{photo.title}</p>
                  </div>
                </div>
              );
            })}
            
            {/* Upload Area Tile */}
            <div onClick={() => fileInputRef.current?.click()} className="aspect-square border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-center bg-gray-50/50 hover:bg-gray-50 transition-colors cursor-pointer group">
               <UploadCloud size={24} className="text-gray-400 mb-2 group-hover:text-indigo-500 transition-colors" />
               <span className="text-xs font-medium text-gray-500">Adicionar Fotos</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
